/**
 * Custom Box Status Bar Component
 * Displays a sticky status bar when user has items in their custom box selection
 * Similar to wildgrain-member-portal MobileBoxSelectionButton
 */

/**
 * Portal-variant detection, shared with custom-box-preview-modal.js (first
 * script to run defines it — both load on every page). True when the visitor
 * runs the portal builder, in which case the theme box chrome restyles to the
 * portal look and routes to the builder instead of adding to cart.
 *
 * builder_portal_design_test concluded and the portal design won, so this is
 * now true by DEFAULT — no Statsig wait, nothing to resolve after paint. The
 * only way to false is an explicit QA opt-out: ?wg_portal=0 in the URL, or
 * wg_builder_portal set to 'control' in localStorage (?wg_portal=1 opts back
 * in). The React builder reads the same key, so the two stay in sync.
 */
if (!window.wgPortalThemeMode) {
  window.wgPortalThemeMode = function () {
    try {
      var qp = new URLSearchParams(window.location.search).get('wg_portal');
      if (qp === '1' || qp === 'portal') {
        localStorage.removeItem('wg_builder_portal');
      } else if (qp === '0' || qp === 'off') {
        localStorage.setItem('wg_builder_portal', 'control');
      }
    } catch (e) { /* private mode */ }
    // wg_builder_portal is the single opt-out key, deliberately: the old
    // wg_portal_assignment key still holds a stale 'control' for everyone the
    // experiment bucketed that way, and reading it would strand them on the
    // pre-portal chrome forever.
    try {
      if (localStorage.getItem('wg_builder_portal') === 'control') return false;
    } catch (e) { /* private mode */ }
    return true;
  };
}

class CustomBoxStatusBar extends HTMLElement {
  constructor() {
    super();
    this.boxState = null;
    this.products = [];
    this.isVisible = false;
    this.previewButton = null;
    this.itemCountElement = null;
    this.totalPriceElement = null;
    // Portal: one-time extras living as REAL cart lines (checkout upsells,
    // deals surfaces, previous sessions). The box modal and checkout count
    // them, so the dock's extras/badge must too or the two disagree.
    this.cartExtrasCount = 0;
  }

  connectedCallback() {
    // Portal visitors get the portal-styled bar (coral/cream, segmented
    // progress, no emoji/confetti). Classic (control) markup/behavior is
    // untouched when the class is absent.
    this.isPortalMode = !!(window.wgPortalThemeMode && window.wgPortalThemeMode());
    if (this.isPortalMode) {
      this.classList.add('portal-mode');
      // Page-level portal marker (independent of dock visibility) so fixed
      // furniture like the Richpanel launcher can be styled for portal
      // visitors even before the dock has anything to show.
      document.documentElement.classList.add('wg-portal-theme');
    }
    this.render();
    this.attachEventListeners();
    this.loadProducts();

    // Sync cart with box state on initial load. Portal waits for extras so
    // an extras-only cart doesn't hide the dock for a beat, then pop in.
    this.syncCartOnLoad().then(async () => {
      if (this.isPortalMode) await this.refreshCartExtras();
      else this.checkVisibility();
    });

    // The builder app mounts asynchronously — the bar could win the race and
    // flash before the app renders and takes over the page. Re-check shortly
    // after load so the dock stands down once the builder is up.
    if (this.isPortalMode) {
      setTimeout(() => this.checkVisibility(), 1000);
      setTimeout(() => this.checkVisibility(), 3000);
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.refreshCartExtras();
      });
      // Keep the Richpanel launcher riding the dock (CSS reads the class +
      // clearance variable) as the dock's height responds to resizes.
      window.addEventListener('resize', () => this.syncDockClearance());
    }

    // The bundle app announces it has taken over the off-PDP Cart pill
    // (html.wg-app-cart-dock + this event — see MiniCartPill in the app).
    // Re-check so this dock stands down the moment the app pill is live,
    // and comes back if the app ever unmounts.
    if (this.isPortalMode) {
      window.addEventListener('wg:app-cart-dock', () => this.checkVisibility());
    }

    // Listen for localStorage changes from other tabs/windows
    window.addEventListener('storage', () => this.checkVisibility());
    // Listen for custom events from the same page
    document.addEventListener('custom-box-state:updated', () => this.checkVisibility());
    window.addEventListener('customBoxStateChanged', () => {
      this.checkVisibility();
    });

    // Listen for cart changes (the box modal dispatches cart:updated with the
    // fresh cart payload after its stepper edits — reuse it, no refetch)
    document.addEventListener('cart:updated', (e) => this.syncCartAndUpdate(e && e.detail && e.detail.cart));
    document.addEventListener('cart:item:added', () => this.syncCartAndUpdate());
    document.addEventListener('cart:refresh', () => this.syncCartAndUpdate());

    this.setupScrollBehavior();
  }

  async syncCartOnLoad() {
    // Only sync if we have a box state
    const boxStateStr = localStorage.getItem('wildgrain-custom-box-state');
    if (!boxStateStr) return;

    // Sync cart items with box state
    // await this.syncCartWithBoxState();
  }

  async syncCartAndUpdate(cart = null) {
    // await this.syncCartWithBoxState();
    if (this.isPortalMode) await this.refreshCartExtras(cart);
    this.checkVisibility();
  }

  /**
   * Portal: count one-time extras sitting in the real Shopify cart — same
   * classification as the box modal's loadCartAddons(): every line that isn't
   * the box (_rb_id / "custom box" / WGB_ placeholder) or a subscription line
   * ships as an extra in this box. Accepts a cart payload (from cart:updated)
   * to skip the refetch.
   */
  async refreshCartExtras(cartData = null) {
    if (!this.isPortalMode) return;
    try {
      let cart = cartData;
      if (!cart) {
        const res = await fetch('/cart.js');
        if (!res.ok) return;
        cart = await res.json();
      }
      let count = 0;
      (cart.items || []).forEach((item) => {
        if (item.properties && item.properties._rb_id) return;
        if (item.selling_plan_allocation) return;
        const sku = String(item.sku || '').toUpperCase();
        if (sku.startsWith('WGB_')) return;
        const title = String(item.product_title || item.title || '').toLowerCase();
        if (title.includes('custom box')) return;
        if (String(item.url || '').includes('selling_plan=')) return;
        count += item.quantity || 1;
      });
      this.cartExtrasCount = count;
      // Visibility keys off this count too (extras-only carts must still
      // show the dock off the PDP). Always re-check — not just the badge.
      this.checkVisibility();
    } catch (e) {
      // Network hiccup — keep the last known count.
    }
  }

  async syncCartWithBoxState() {
    try {
      // Fetch current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Filter items that don't have WGB_ SKU (these are add-ons from cart)
      const cartAddons = cart.items.filter(item => {
        const sku = item.sku || '';
        return !sku.startsWith('WGB_') && (sku.startsWith('WG_') || sku.startsWith('WGP_'));
      });
  } catch (error) {
      console.error('Error syncing cart with box state:', error);
    }
  }

  disconnectedCallback() {
    this.cancelBuilderConfirm();
    window.removeEventListener('storage', () => this.checkVisibility());
    document.removeEventListener('custom-box-state:updated', () => this.checkVisibility());
    window.removeEventListener('customBoxStateChanged', () => this.checkVisibility());
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
    }
  }

  setupScrollBehavior() {
    // Portal Cart pill never scroll-hides: with items in the cart it must
    // stay visible on mobile so the way back to the box is always on screen
    // (matches the app's MiniCartPill, which dropped its scroll-hide too).
    if (this.isPortalMode) return;

    this._lastScrollY = window.scrollY;
    this._scrollHidden = false;

    const SCROLL_DELTA = 8;
    const MIN_SCROLL = 120;
    const isProductPage = () => /^\/products\/[^/?#]+/.test(window.location.pathname);
    const isMobile = () => window.innerWidth < 1024;

    // Single toggle point: the html mirror class lets the Richpanel launcher
    // (fixed, outside this element) slide away in the same frame as the dock.
    // Portal-only — control's launcher never followed the bar.
    const setScrollHidden = (hidden) => {
      if (this._scrollHidden === hidden) return;
      this._scrollHidden = hidden;
      this.classList.toggle('scroll-hidden', hidden);
      // The html mirror class is the app pill's to manage while it owns the
      // dock (isAppDockActive) — touching it here would strip the state the
      // app just set.
      if (this.isPortalMode && !this.isAppDockActive()) {
        document.documentElement.classList.toggle('wg-portal-dock-scroll-hidden', hidden);
      }
    };

    this._scrollHandler = () => {
      if (isProductPage() || !isMobile() || !this.isVisible) {
        setScrollHidden(false);
        this._lastScrollY = window.scrollY;
        return;
      }

      const currentY = window.scrollY;

      if (currentY < MIN_SCROLL) {
        setScrollHidden(false);
        this._lastScrollY = currentY;
        return;
      }

      if (currentY > this._lastScrollY + SCROLL_DELTA) {
        setScrollHidden(true);
      } else if (currentY < this._lastScrollY - SCROLL_DELTA) {
        setScrollHidden(false);
      }

      this._lastScrollY = currentY;
    };

    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  async loadProducts() {
    try {
      // Check if products are cached in localStorage
      const cachedProducts = localStorage.getItem('wildgrain-products');
      const cacheTimestamp = localStorage.getItem('wildgrain-products-timestamp');
      const now = Date.now();
      const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : Infinity;
      const CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours

      if (cachedProducts && cacheAge < CACHE_DURATION) {
        this.products = JSON.parse(cachedProducts);
        this.updateDisplay();
        return;
      }

      // Fetch products from Shopify with CHECKOUT_ADDON tag
      const response = await fetch('/collections/addons-checkout-hide/products.json?limit=150&fields=id,title,body_html,product_type,tags,images,variants');
      const data = await response.json();

      // Process products (only keep necessary data)
      this.products = this.processProducts(data.products);

      // Cache in localStorage
      localStorage.setItem('wildgrain-products', JSON.stringify(this.products));
      localStorage.setItem('wildgrain-products-timestamp', now.toString());

      this.updateDisplay();
    } catch (error) {
      console.error('Error loading products for custom box status bar:', error);
    }
  }

  processProducts(products) {
    return products.reduce((acc, product) => {
      // Find first variant with WG_ or WGP_ SKU
      const validVariant = product.variants.find(v =>
        v.sku?.startsWith('WG_') || v.sku?.startsWith('WGP_')
      );

      if (!validVariant) {
        return acc;
      }

      acc.push({
        id: product.id,
        title: product.title,
        handle: product.handle,
        body_html: product.body_html,
        product_type: product.product_type,
        tags: product.tags,
        image: product.images && product.images.length > 0 ? product.images[0].src : null,
        variants: [{
          id: validVariant.id,
          sku: validVariant.sku,
          price: validVariant.price,
          compare_at_price: validVariant.compare_at_price
        }]
      });

      return acc;
    }, []);
  }

  /**
   * True when the builder app has actually rendered on this page (e.g.
   * /pages/box — a plain `page` template where the builder-template Statsig
   * suppression CSS never runs, yet the footer app block renders the full
   * builder). The portal builder has its own bottom bar there, so the theme
   * dock must stand down. The empty root div exists on EVERY page, hence the
   * childElementCount check; the app renders async, so this is evaluated on
   * every visibility pass instead of once at connect.
   */
  isBuilderActiveOnPage() {
    if (!this.isPortalMode) return false;
    const root = document.getElementById('wildgrain-box-builder-root');
    // The footer app block mounts (hidden) on every page — only a VISIBLE
    // builder counts, so check rendered height rather than presence.
    return !!(root && root.childElementCount > 0 && root.getBoundingClientRect().height > 0);
  }

  /**
   * Hide only if the builder is still active after a grace period. Guards
   * against the app's mount-time layout transient (the root briefly reports
   * full height on every page) tearing the dock down on non-builder pages.
   */
  scheduleBuilderConfirm() {
    if (this._builderConfirmTimer) return;
    this._builderConfirmTimer = setTimeout(() => {
      this._builderConfirmTimer = null;
      if (this.isBuilderActiveOnPage()) {
        this.hide();
      } else {
        this.checkVisibility();
      }
    }, 1500);
  }

  cancelBuilderConfirm() {
    if (this._builderConfirmTimer) {
      clearTimeout(this._builderConfirmTimer);
      this._builderConfirmTimer = null;
    }
  }

  /**
   * True while the bundle app owns the off-PDP Cart pill (its MiniCartPill
   * flags html.wg-app-cart-dock for as long as it's mounted). The app's
   * pill and drawer share one source of truth for the badge, so this dock —
   * whose badge math is a theme-side duplicate — must stand down or the two
   * can disagree. Without the app (script failed, page without the block),
   * the class never appears and this dock keeps working as the fallback.
   */
  isAppDockActive() {
    if (!this.isPortalMode) return false;
    return document.documentElement.classList.contains('wg-app-cart-dock');
  }

  /**
   * True on pages where the portal builder WILL render (bundle PDP
   * templates — body carries the template suffix as a class — and
   * /pages/box). The app mounts async, so waiting for
   * isBuilderActiveOnPage() alone lets the theme dock flash for a beat
   * before the app's own bar takes over; on these pages the dock never
   * shows at all.
   */
  isBuilderPage() {
    if (!this.isPortalMode) return false;
    const bodyCls = (document.body && document.body.classList) || null;
    if (bodyCls && (bodyCls.contains('recharge-bundle') || bodyCls.contains('wildgrain-custom'))) return true;
    return /^\/pages\/box(?:$|[/?#])/.test(window.location.pathname);
  }

  hasPositiveQty(map) {
    return Object.values(map || {}).some((qty) => qty > 0);
  }

  /**
   * Anything the off-PDP dock should surface. Box picks always count.
   * Portal also counts pending add-on picks and one-time extras already in
   * the Shopify cart — otherwise an extras-only cart hides the Cart pill
   * the moment the shopper leaves the builder PDP.
   */
  hasCartToShow() {
    const boxState = this.boxState || {};
    if (this.hasPositiveQty(boxState.productQuantities)) return true;
    if (!this.isPortalMode) return false;
    return this.hasPositiveQty(boxState.addonQuantities) || (this.cartExtrasCount || 0) > 0;
  }

  checkVisibility() {
    try {
      if (this.isAppDockActive()) {
        this.cancelBuilderConfirm();
        this.hide();
        return;
      }
      if (this.isBuilderPage()) {
        this.cancelBuilderConfirm();
        this.hide();
        return;
      }
      if (this.isBuilderActiveOnPage()) {
        // The app mounts on EVERY page and briefly lays out at full height
        // before standing down off the builder templates — hiding instantly
        // here made the dock flicker on page load (show → hide → re-show a
        // few seconds later). Keep the current state and only hide once the
        // builder is STILL active after a confirmation window; the mount
        // transient never survives it, a real builder page does.
        this.scheduleBuilderConfirm();
        return;
      }
      this.cancelBuilderConfirm();
      const boxStateStr = localStorage.getItem('wildgrain-custom-box-state');
      if (boxStateStr) {
        this.boxState = JSON.parse(boxStateStr);
      } else if (this.isPortalMode && (this.cartExtrasCount || 0) > 0) {
        // Extras landed in the live cart without a saved box (checkout
        // upsell, previous session). Still show the dock so they're reachable.
        this.boxState = this.boxState || { productQuantities: {}, addonQuantities: {} };
      } else {
        this.hide();
        return;
      }

      if (this.hasCartToShow()) {
        this.show();
        this.updateDisplay();
      } else {
        this.hide();
      }
    } catch (error) {
      console.error('Error checking custom box visibility:', error);
      this.hide();
    }
  }

  show() {
    if (!this.isVisible) {
      this.isVisible = true;
      this.classList.remove('hidden');
      // Use modern approach without forcing reflow
      // Add active class on next frame to trigger animation.
      // Guard against a stale frame: if hide() ran meanwhile, re-adding
      // .active would win over .hidden in the cascade and un-hide the bar
      // (seen when the builder app takes over the page right after load).
      requestAnimationFrame(() => {
        if (this.isVisible) this.classList.add('active');
        this.syncDockClearance();
      });
    }
  }

  hide() {
    if (this.isVisible) {
      this.isVisible = false;
      this.classList.add('hidden');
      this.classList.remove('active');
    }
    this.syncDockClearance();
  }

  /**
   * Portal: publish the dock's footprint so fixed page furniture (the
   * Richpanel chat launcher) can dock itself against the bar instead of
   * floating over it. Sets html.wg-portal-dock-open plus
   * --wg-dock-clearance = dock height + its bottom offset (measured from
   * computed style, not rects — the show/hide transform animation would
   * skew rect readings). No-ops entirely for control visitors.
   */
  syncDockClearance() {
    if (!this.isPortalMode) return;
    // The app's MiniCartPill manages wg-portal-dock-open / --wg-dock-clearance
    // while it owns the pill — hands off entirely, or this dock's hide()
    // would strip the classes right after the app set them.
    if (this.isAppDockActive()) return;
    const html = document.documentElement;
    if (!this.isVisible) {
      html.classList.remove('wg-portal-dock-open');
      html.style.removeProperty('--wg-dock-clearance');
      return;
    }
    const bottomOffset = parseFloat(getComputedStyle(this).bottom) || 0;
    const clearance = Math.round(this.offsetHeight + bottomOffset);
    html.classList.add('wg-portal-dock-open');
    html.style.setProperty('--wg-dock-clearance', `${clearance}px`);
  }

  updateDisplay() {
    // Portal dock needs no product catalog (it shows counts only — the priced
    // total lives in the box modal's summary, same as the app's bottom bar).
    if (!this.boxState) return;
    if (!this.isPortalMode && !this.products.length) return;

    const productQuantities = this.boxState.productQuantities || {};
    const freeProductId = this.boxState?.freeProduct;

    // Calculate totals:
    // - Exclude free product from paid item count and box size limit
    // - Always count the free item as 1 toward the displayed total when present
    let totalItems = 0;
    const freeItemCount = freeProductId ? 1 : 0;
    Object.entries(productQuantities).forEach(([productId, qty]) => {
      totalItems += qty;
    });

    const boxSize = this.getBoxSize(this.boxState.selectedSize);
    const progress = Math.min((totalItems / boxSize) * 100, 100);

    // Portal variant renders the off-PDP Cart pill and skips the classic
    // emoji/price/confetti pipeline entirely.
    // Extras = the builder's pending add-on picks (addonQuantities, persisted
    // by the app) + one-time lines already in the real cart — the same two
    // sources the box modal and the app's tray display, so all three agree.
    if (this.isPortalMode) {
      const pendingExtras = Object.values(this.boxState.addonQuantities || {})
        .reduce((sum, qty) => sum + (qty > 0 ? qty : 0), 0);
      this.updatePortalDock(totalItems, boxSize, pendingExtras + (this.cartExtrasCount || 0));
      return;
    }

    const progressBar = this.querySelector('[data-progress-bar]');
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }

    // Update item count with directive instruction text
    if (this.itemCountElement) {
      const addOnsCount = Math.max(0, totalItems - boxSize);

      // Generate playful instruction text with emojis based on box state
      let instructionText = '';
      let emoji = '';

      if (totalItems >= boxSize) {
        if (addOnsCount > 0) {
          instructionText = `Box ready + ${addOnsCount} add-on${addOnsCount > 1 ? 's' : ''}`;
          emoji = '🎉';
        } else {
          instructionText = 'Box ready';
          emoji = '✨';
        }
      } else {
        const itemsNeeded = boxSize - totalItems;
        const percentComplete = Math.round(progress);

        if (percentComplete >= 75) {
          instructionText = `${itemsNeeded} more item${itemsNeeded > 1 ? 's' : ''}`;
          emoji = '🔥';
        } else if (percentComplete >= 50) {
          instructionText = `${itemsNeeded} more item${itemsNeeded > 1 ? 's' : ''}`;
          emoji = '💪';
        } else {
          instructionText = `${itemsNeeded} more item${itemsNeeded > 1 ? 's' : ''}`;
          emoji = '🚀';
        }
      }

      this.itemCountElement.innerHTML = `<span class="emoji-icon">${emoji}</span> ${instructionText}`;

      // Color coding: Red when not at capacity, Green when at or over capacity
      if (totalItems >= boxSize) {
        this.itemCountElement.classList.add('text-green-500', 'font-bold');
        this.itemCountElement.classList.remove('text-red-500');

        // Add celebration animation when box is full
        if (!this.classList.contains('box-full')) {
          this.classList.add('box-full');

          // Trigger confetti effect
          this.triggerConfetti();

          // Remove the animation class after it completes to allow it to retrigger
          setTimeout(() => {
            // Keep the class for the rainbow border but reset the bounce/wiggle
            // by removing and re-adding
          }, 2000);
        }
      } else {
        this.itemCountElement.classList.add('text-red-500', 'font-bold');
        this.itemCountElement.classList.remove('text-green-500');

        // Remove celebration class when box is no longer full
        this.classList.remove('box-full');
      }
    }

    if (this.totalPriceElement) {
      const totalPrice = this.calculateTotalPrice(productQuantities);
      const isFO = (function() { try { return sessionStorage.getItem('wg_friend_offer') === 'true'; } catch(e) { return false; } })();
      const discountAmount = isFO ? totalPrice : this.getDiscountAmount();
      const finalPrice = Math.max(0, totalPrice - discountAmount);
      if (totalPrice > 0) {
        if (isFO) {
          this.totalPriceElement.innerHTML = `<s style="opacity:0.45;font-weight:400;font-size:0.85em">$${totalPrice.toFixed(0)}</s> <span style="color:#059669">FREE</span>`;
        } else if (discountAmount > 0) {
          this.totalPriceElement.innerHTML = `<s style="opacity:0.45;font-weight:400;font-size:0.85em">$${totalPrice.toFixed(0)}</s> <span style="color:#059669">$${finalPrice.toFixed(0)}</span>`;
        } else {
          this.totalPriceElement.textContent = `$${totalPrice.toFixed(0)}`;
        }
        this.totalPriceElement.style.display = '';
      } else {
        this.totalPriceElement.innerHTML = '';
        this.totalPriceElement.style.display = 'none';
      }
    }

    // Update button text and glow based on box full status
    this.updateButtonState(totalItems + freeItemCount, boxSize + freeItemCount);
  }

  /**
   * Portal dock updater — the theme dock only shows off the bundle PDP
   * (the app renders the full bar there). Off-PDP we show the Cart pill
   * only; the badge and aria-label stay in sync with box + extras.
   */
  updatePortalDock(totalItems, boxSize, extrasCount) {
    const isFull = totalItems >= boxSize;
    // Overflow box picks (legacy states) count as extras too, alongside the
    // pending picks + cart lines passed in.
    const extras = Math.max(0, totalItems - boxSize) + (extrasCount || 0);
    const boxCount = Math.min(totalItems, boxSize);

    // Cart badge + accessible progress on the button. Same arithmetic as
    // the app's Cart pill: box picks + extras, free item excluded.
    const badgeCount = boxCount + extras;
    const badge = this.querySelector('[data-cart-badge]');
    if (badge) {
      badge.textContent = String(badgeCount);
      if (badgeCount > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    }
    if (this.previewButton) {
      this.previewButton.setAttribute(
        'aria-label',
        isFull
          ? `Cart, ${badgeCount} items — review your box and add extras`
          : `Cart, ${boxCount} of ${boxSize} items picked${extras > 0 ? ` plus ${extras} extra${extras === 1 ? '' : 's'}` : ''} — view your box`
      );
    }

    this.syncDockClearance();
  }

  triggerConfetti() {
    // Use DocumentFragment to batch DOM insertion - prevents layout thrashing
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'confetti-container';
    const fragment = document.createDocumentFragment();

    // Create 30 confetti pieces in fragment
    for (let i = 0; i < 30; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = `${Math.random() * 100}%`;
      confetti.style.animationDelay = `${Math.random() * 0.5}s`;
      confetti.style.backgroundColor = this.getRandomConfettiColor();
      fragment.appendChild(confetti);
    }

    // Single DOM append to minimize layout shift
    confettiContainer.appendChild(fragment);
    this.appendChild(confettiContainer);

    // Remove confetti after animation
    setTimeout(() => {
      confettiContainer.remove();
    }, 3000);
  }

  getRandomConfettiColor() {
    const colors = ['#f86b57', '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  updateButtonState(totalItemsWithFree, displayCapacity) {
    if (!this.previewButton) return;

    const buttonText = this.previewButton.querySelector('span');
    const buttonIcon = this.previewButton.querySelector('svg');
    const isBoxFull = totalItemsWithFree >= displayCapacity;
    
    if (isBoxFull) {
      if (buttonText) {
        buttonText.textContent = 'VIEW BOX';
      }
      if (buttonIcon) {
        buttonIcon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>';
        buttonIcon.setAttribute('viewBox', '0 0 24 24');
        buttonIcon.style.display = '';
      }
      this.previewButton.classList.remove('custom-box-status-bar__btn--glow');
      this.previewButton.setAttribute('aria-label', 'View your custom box');
    } else {
      if (buttonText) {
        buttonText.textContent = 'VIEW BOX';
      }
      if (buttonIcon) {
        buttonIcon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>';
        buttonIcon.setAttribute('viewBox', '0 0 24 24');
        buttonIcon.style.display = '';
      }
      this.previewButton.classList.remove('custom-box-status-bar__btn--glow');
      this.previewButton.setAttribute('aria-label', 'View your custom box selection');
    }
    
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  getDiscountAmount() {
    if (!this.boxState) return 0;

    const selectedSize = this.boxState.selectedSize || '';
    const is4Items = selectedSize.toLowerCase().includes('4 items');
    if (is4Items) return 0;

    const discount = this.boxState.discount;
    if (discount && discount.amount > 0) {
      return parseFloat(discount.amount);
    }

    const autoDiscount = this.getCookie('wg_auto_discount');
    if (autoDiscount) {
      const raw = this.getCookie('wg_auto_discount_amount');
      const parsed = raw != null && raw !== '' ? parseFloat(raw) : NaN;
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 20;
    }

    return 0;
  }

  getBoxSize(selectedSize) {
    if (!selectedSize) return 7;
    const match = selectedSize.match(/(\d+)/);
    return match ? parseInt(match[1]) : 7;
  }

  calculateTotalPrice(productQuantities) {
    const planPrice = parseFloat(this.boxState?.selectedPlanPrice) || 0;
    const boxSize = this.getBoxSize(this.boxState?.selectedSize);
    const freeProductId = this.boxState?.freeProduct;

    let totalItems = 0;
    Object.entries(productQuantities || {}).forEach(([productId, qty]) => {
      totalItems += qty;
    });

    let addonPrice = 0;
    if (totalItems > boxSize) {
      let itemsProcessed = 0;
      Object.entries(productQuantities).forEach(([productId, quantity]) => {
        if (freeProductId && String(productId) === String(freeProductId)) return;
        const product = this.products.find(p => String(p.id) === String(productId));
        if (product && product.variants && product.variants[0]) {
          const price = parseFloat(product.variants[0].price) || 0;
          const itemsAfterThisProduct = itemsProcessed + quantity;

          if (itemsAfterThisProduct > boxSize) {
            const addonQuantity = Math.min(quantity, itemsAfterThisProduct - boxSize);
            addonPrice += price * addonQuantity;
          }

          itemsProcessed += quantity;
        }
      });
    }

    return totalItems > boxSize ? planPrice + addonPrice : planPrice;
  }

  openPreviewModal() {
    // Portal mode: one cart everywhere — the bundle app owns the box drawer
    // on every page, not just the builder PDP. Cancelable handshake mirrors
    // wg:open-product-detail: the app preventDefaults when it's mounted and
    // opens its own drawer; if it never loaded, the event completes
    // unhandled and we fall back to the theme's modal below. Classic
    // (non-portal) visitors never dispatch and keep the theme modal.
    if (this.isPortalMode) {
      const request = new CustomEvent('wg:open-box-drawer', { cancelable: true });
      const handledByApp = !window.dispatchEvent(request);
      if (handledByApp) return;
    }
    const modal = document.querySelector('custom-box-preview-modal');
    if (modal) {
      modal.open(true);
    }
  }

  handleButtonClick() {
    this.openPreviewModal();
  }

  addToCartDirectly(boxState) {
    const btn = this.previewButton;
    const btnText = btn?.querySelector('span');
    const originalText = btnText?.textContent;

    if (btnText) btnText.textContent = 'ADDING...';
    if (btn) btn.disabled = true;

    try {
      window.dispatchEvent(new CustomEvent('wildgrain-box-builder:addToCart', {
        detail: { boxState }
      }));

      const handleSuccess = () => {
        if (btnText) btnText.textContent = originalText;
        if (btn) btn.disabled = false;
        setTimeout(() => {
          const checkoutAddonsDrawer = document.querySelector('checkout-addons-drawer');
          if (checkoutAddonsDrawer && typeof checkoutAddonsDrawer.open === 'function') {
            checkoutAddonsDrawer.open();
          }
        }, 300);
        window.removeEventListener('wildgrain-box-builder:addToCartError', handleError);
      };

      const handleError = (event) => {
        if (btnText) btnText.textContent = originalText;
        if (btn) btn.disabled = false;
        window.dispatchEvent(new CustomEvent('wildgrain-toast', {
          detail: { message: `Failed to add to cart: ${event.detail.error}. Please try again.`, type: 'error', duration: 7000 }
        }));
        window.removeEventListener('wildgrain-box-builder:addToCartSuccess', handleSuccess);
      };

      window.addEventListener('wildgrain-box-builder:addToCartSuccess', handleSuccess, { once: true });
      window.addEventListener('wildgrain-box-builder:addToCartError', handleError, { once: true });
    } catch (error) {
      if (btnText) btnText.textContent = originalText;
      if (btn) btn.disabled = false;
      window.dispatchEvent(new CustomEvent('wildgrain-toast', {
        detail: { message: `Failed to add to cart: ${error.message}. Please try again.`, type: 'error', duration: 7000 }
      }));
    }
  }

  attachEventListeners() {
    this.previewButton = this.querySelector('[data-preview-button]');
    this.itemCountElement = this.querySelector('[data-item-count]');
    this.totalPriceElement = this.querySelector('[data-total-price]');

    if (this.previewButton) {
      this.previewButton.addEventListener('click', () => this.handleButtonClick());
    }

    // Portal dock: swipe up on the Cart pill expands into the box modal,
    // mirroring the app's grab-tab gesture on the PDP bar.
    if (this.isPortalMode) {
      let touchStartY = null;
      this.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      this.addEventListener('touchmove', (e) => {
        if (touchStartY == null) return;
        if (touchStartY - e.touches[0].clientY > 24) {
          touchStartY = null;
          this.openPreviewModal();
        }
      }, { passive: true });
      this.addEventListener('touchend', () => { touchStartY = null; });
    }
  }

  render() {
    // Portal variant: off-PDP cart-only dock (the app's full bar owns the
    // PDP). Classic markup below stays byte-identical for control.
    if (this.isPortalMode) {
      this.innerHTML = `
        <div class="wg-dock__row">
          <button type="button" class="wg-dock__cart" data-preview-button aria-haspopup="dialog" aria-label="Cart — view your box">
            <span class="wg-dock__bag" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 9.5h15l-1.2 9.1a2 2 0 01-1.98 1.74H7.68a2 2 0 01-1.98-1.74L4.5 9.5zM8 9.5V7a4 4 0 118 0v2.5"/>
              </svg>
            </span>
            <span class="wg-dock__cart-label">Cart</span>
            <span class="wg-dock__badge" data-cart-badge hidden>0</span>
          </button>
        </div>
      `;
      return;
    }
    this.innerHTML = `
      <div class="custom-box-status-bar__container">
        <div class="custom-box-status-bar__progress-wrapper">
          <div class="custom-box-status-bar__progress-bar">
            <div class="custom-box-status-bar__progress-fill" data-progress-bar></div>
          </div>
        </div>
        <div class="custom-box-status-bar__header">
          <div class="custom-box-status-bar__info">
            <span class="custom-box-status-bar__count" data-item-count>🚀 Let's build! 7 more items</span>
            <span class="custom-box-status-bar__price" data-total-price></span>
          </div>
          <div class="custom-box-status-bar__actions">
            <button class="custom-box-status-bar__btn custom-box-status-bar__btn--preview" data-preview-button aria-label="View your custom box selection">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
              <span>VIEW BOX</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('custom-box-status-bar', CustomBoxStatusBar);
